import type { ChartRow, ChartScalar, ChartSeries } from './types';

export const MIN_PX_PER_POINT = 6;
export const MIN_CANDLESTICK_PX = 4;
export const FALLBACK_PLOT_WIDTH_PX = 800;
export const NAVIGATOR_MIN_POINTS = 8;

export type SeriesAggregation = 'first' | 'last' | 'max' | 'min' | 'mean' | 'sum';

export function targetDisplayPointCount(widthPx: number): number {
  const width = Number.isFinite(widthPx) && widthPx > 0 ? widthPx : FALLBACK_PLOT_WIDTH_PX;
  return Math.max(2, Math.floor(width / MIN_PX_PER_POINT));
}

export function shouldRenderCandlesticks(widthPx: number, pointCount: number): boolean {
  if (pointCount <= 1) return true;
  const width = Number.isFinite(widthPx) && widthPx > 0 ? widthPx : FALLBACK_PLOT_WIDTH_PX;
  return width / pointCount >= MIN_CANDLESTICK_PX;
}

export function seriesAggregations(series: ChartSeries[]): Record<string, SeriesAggregation> {
  const aggregations: Record<string, SeriesAggregation> = {};
  for (const item of series) {
    if (item.mark === 'candlestick' && item.fieldMapping) {
      aggregations[item.fieldMapping.open] = 'first';
      aggregations[item.fieldMapping.high] = 'max';
      aggregations[item.fieldMapping.low] = 'min';
      aggregations[item.fieldMapping.close] = 'last';
    } else if (item.role === 'volume' && item.dataKey) {
      aggregations[item.dataKey] = 'sum';
    } else if (item.dataKey) {
      aggregations[item.dataKey] = 'last';
    }
  }
  return aggregations;
}

function numericValues(values: ChartScalar[]): number[] {
  return values
    .filter(
      (value) =>
        value !== null && value !== true && value !== false && !(String(value).trim() === ''),
    )
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));
}

function numericValue(value: ChartScalar | undefined): number | null {
  if (
    value === null ||
    value === undefined ||
    value === true ||
    value === false ||
    String(value).trim() === ''
  )
    return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function aggregateValues(values: ChartScalar[], aggregation: SeriesAggregation): number | null {
  if (aggregation === 'first') return numericValue(values[0]);
  if (aggregation === 'last') return numericValue(values.at(-1));
  const numbers = numericValues(values);
  if (numbers.length === 0) return null;
  if (aggregation === 'max') return Math.max(...numbers);
  if (aggregation === 'min') return Math.min(...numbers);
  if (aggregation === 'sum') return numbers.reduce((sum, value) => sum + value, 0);
  if (aggregation === 'mean') {
    return numbers.reduce((sum, value) => sum + value, 0) / numbers.length;
  }
  return null;
}

export function bucketPlotRows<T extends ChartRow>(
  rows: T[],
  targetCount: number,
  aggregations: Record<string, SeriesAggregation>,
): T[] {
  if (rows.length <= targetCount || targetCount < 2) return rows;
  const buckets: T[][] = Array.from({ length: targetCount }, () => []);
  const bucketStarts: number[] = [];
  const bucketEnds: number[] = [];
  rows.forEach((row, index) => {
    const bucketIndex = Math.min(targetCount - 1, Math.floor((index * targetCount) / rows.length));
    const sourceIndex = plottedIndex(row, index);
    if (buckets[bucketIndex].length === 0) {
      bucketStarts[bucketIndex] = numericValue(row.__chartBucketStartIndex) ?? sourceIndex;
    }
    bucketEnds[bucketIndex] = numericValue(row.__chartBucketEndIndex) ?? sourceIndex;
    buckets[bucketIndex].push(row);
  });
  return buckets.map((bucket, bucketIndex) => {
    const last = bucket[bucket.length - 1];
    const next: ChartRow = {
      ...last,
      __chartBucketStartIndex: bucketStarts[bucketIndex],
      __chartBucketEndIndex: bucketEnds[bucketIndex],
    };
    const keys = new Set(bucket.flatMap((row) => Object.keys(row)));
    keys.forEach((key) => {
      if (
        key === '__chartIndex' ||
        key === '__chartX' ||
        key === '__chartBucketStartIndex' ||
        key === '__chartBucketEndIndex'
      )
        return;
      const aggregation = aggregations[key];
      if (!aggregation) return;
      next[key] = aggregateValues(
        bucket.map((row) => row[key]),
        aggregation,
      );
    });
    // SAFETY: `next` starts as T and only receives ChartScalar values on keys already
    // accepted by the ChartRow contract, so the generic row subtype remains intact.
    return next as T;
  });
}

function plottedIndex(row: ChartRow, fallback: number): number {
  return numericValue(row.__chartIndex) ?? fallback;
}

/** Resolve hover against the same aggregate row used to draw the chart. */
export function displayedRowForIndex<T extends ChartRow>(
  rows: T[],
  activeIndex: number,
): T | undefined {
  if (!Number.isFinite(activeIndex)) return undefined;
  return rows.find((row, index) => {
    const sourceIndex = plottedIndex(row, index);
    const start = numericValue(row.__chartBucketStartIndex) ?? sourceIndex;
    const end = numericValue(row.__chartBucketEndIndex) ?? sourceIndex;
    return activeIndex >= start && activeIndex <= end;
  });
}

export function clampVisibleRange(
  start: number,
  end: number,
  lastIndex: number,
  minimumSpan = 3,
): [number, number] {
  const last = Math.max(0, lastIndex);
  const span = Math.min(Math.max(1, minimumSpan), last);
  let nextStart = Math.max(0, Math.min(start, last));
  let nextEnd = Math.max(0, Math.min(end, last));
  if (nextEnd < nextStart) {
    const swap = nextStart;
    nextStart = nextEnd;
    nextEnd = swap;
  }
  if (nextEnd - nextStart < span) {
    nextEnd = Math.min(last, nextStart + span);
    nextStart = Math.max(0, nextEnd - span);
  }
  return [nextStart, nextEnd];
}
